import { CommonModule } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Component, ElementRef, inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import * as signalR from '@microsoft/signalr';
import html2canvas from 'html2canvas';
import { DocumentMasterService } from 'src/app/Common/DocumentMasterService/document-master.service';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { SharedModule } from 'src/app/Shared/shared.module';
import { environment } from 'src/environments/environment';
import { SupportTicketsCreateRequestDTO, SupportTicketsDTO, SupportTicketsList, SupportTicketsReadByFromCompanyIdRequestDTO } from '../ticket.interface';
import { SupportProjectMasterDTO, SupportProjectMasterList, SupportProjectMasterReadByTenantIdRequestDTO } from '../support-project-master.interface';
import { TicketAssigneeCreateRequestDTO, TicketAssigneeDTO } from '../ticket/ticket-details/ticket-assignee/ticket-assignee.interface';
import { Subscription } from 'rxjs';
import { ConsoleLogCaptureService } from './console-log-capture.service';
import { SupportWidgetVisibilityService } from './support-widget-visibility.service';
import {
  DiscussionDetailCreateRequestDTO,
  DiscussionDetailDTO,
  DiscussionDetailMarkAsReadRequestDTO,
  DiscussionDetailReadByDiscussionIdRequestDTO,
  DiscussionDetailReadByDiscussionIdResponseDTO,
  DiscussionMasterDTO,
  DiscussionMasterForSalesPersonDTO,
  DiscussionMasterReadAllForSalesPersonRequestDTO,
  DiscussionMasterReadAllForSalesPersonResponseDTO,
  DiscussionMasterReadOrCreateRequestDTO,
  DiscussionSenderType
} from '../discussion.interface';
import { SKIP_TENANT_URL_REWRITE } from '../../api-url-rewrite.interceptor';
import { FORCE_PROVIDER_TENANT_HEADERS } from '../../validation.interceptor';

// Support Desk (chat + tickets) is centralized under TenantId=1 (Advin) —
// every client's row lives in ONE shared database on Advin's own PROD
// server, never on whichever server pool the logged-in tenant is assigned
// to elsewhere. Built directly from environment.SPDBaseURL (the compiled-in
// default: dev.apig.spd.vnccy.com in dev, apig.spd.advinsoftwares.com in
// prod) rather than through GlobalVariables.ts's SPD-derived constants and
// their service wrappers (TicketService/DiscussionService/etc.) — AND every
// call below passes SKIP_REWRITE_OPTIONS, which tags the request two ways:
// ApiUrlRewriteInterceptor leaves its origin alone instead of rewriting it
// to the current tenant's own server pool in production, AND
// ValidationInterceptor sends CompanyId/BranchId pinned to the Provider
// tenant (1/1) instead of the logged-in user's own — CompanyConfigMiddleware
// resolves the DB connection from those headers, and SupportDesk's tables
// only exist in the Provider's database. Both are scoped to this widget's
// own requests only, via HttpContext — NOT a global change: the admin's own
// SupportDesk pages still call the exact same SPD origin through
// TicketService/DiscussionService, without either context token, and keep
// getting rewritten/headered normally.
const SPD_BASE_URL = environment.SPDBaseURL + '/SPD';
const DISCUSSION_MASTER_URL = SPD_BASE_URL + '/DM';
const DISCUSSION_DETAIL_URL = SPD_BASE_URL + '/DD';
const SUPPORT_TICKET_URL = SPD_BASE_URL + '/ST';
const SUPPORT_PROJECT_MASTER_URL = SPD_BASE_URL + '/SPM';
const TICKET_ASSIGNEE_URL = SPD_BASE_URL + '/TAG';
const SKIP_REWRITE_OPTIONS = {
  context: new HttpContext()
    .set(SKIP_TENANT_URL_REWRITE, true)
    .set(FORCE_PROVIDER_TENANT_HEADERS, true)
};

type WidgetView = 'menu' | 'chat' | 'tickets' | 'create-ticket';
// Matches spd.SupportTickets_ReadByTicketOwner's recognized @TicketStatus
// values exactly (see TicketComponent, the /supportdesk/SupportTickets page
// this same data backs) — anything else falls through to that proc's
// "invalid status" branch and returns nothing.
type TicketTab = 'Active' | 'InProgress' | 'Closed';

interface ChatMessage {
  discussionDetailId: number;
  srNo: number;
  senderType: DiscussionSenderType;
  senderId: number;
  senderName: string;
  fromMe: boolean;
  createdOn?: Date;
  // Read-receipt state for a message this side sent — meaningless (and
  // unused) on the other party's messages. Only refreshes on the next
  // history load/thread reopen: MarkAsRead isn't broadcast over the hub, so
  // a checkmark won't flip from single to double live without a reload.
  isRead?: boolean;
  // Rendered via [innerHTML] — mirrors the real DiscussionText column, which
  // stores HTML (text + an <img> for an attached screenshot), not plain
  // text. A message just composed locally (see sendMessage()) carries a
  // SafeHtml, since we built that markup ourselves and know it's safe.
  // Anything fetched from the server (history, or another sender's live
  // push) is a plain string instead and goes through Angular's normal
  // sanitizer — untrusted at rest, even if it's our own past message,
  // since the REST Create API could in principle be hit directly with
  // unescaped content bypassing this UI.
  html: string | SafeHtml;
}

/**
 * Standalone (not declared in SupportDeskModule) so HomeComponent — which
 * lives in the always-loaded AdvERPModule, not a lazy route — can use it
 * without pulling in the rest of the lazy-loaded support-desk feature module.
 *
 * Tickets are real (spd.SupportTickets_ReadByTicketOwner via TicketService —
 * the same data and endpoint the /supportdesk/SupportTickets page uses;
 * clicking a row navigates there for detail/status, this widget never edits
 * a ticket). Creating one is real too, via SupportTicket_Create, but through
 * a deliberately minimal Title/Project/Description form here — not the
 * admin console's full dynamic UIC form.
 *
 * Chat join is socket-first per the backend team's integration reference
 * (2026-09-19): connectAndJoinChat() opens the /discussionHub SignalR
 * connection and invokes JoinMyDiscussionAsClient, which does the same
 * ReadOrCreate lookup as the REST endpoint AND joins the live-push group in
 * one round trip. DiscussionDetail_ReadByDiscussionId (REST) always loads
 * history afterwards — the socket never replays past messages — and
 * DiscussionDetail_Create (REST) always sends. If the socket can't connect
 * or join (e.g. the DEV host's WebSocket Protocol feature isn't enabled
 * yet), ensureDiscussionReadyViaRest() falls back to the plain REST
 * DiscussionMaster_ReadOrCreate so chat still fully works, just without live
 * delivery until a later retry (each Chat-tab reopen retries the socket).
 */
@Component({
  selector: 'app-support-chat-widget',
  standalone: true,
  // SharedModule (for app-rich-text-editor) costs nothing extra here — it's
  // already imported by AdvERPModule, which HomeComponent lives in, so it's
  // loaded on the exact same chunk this widget already ships in.
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedModule],
  templateUrl: './support-chat-widget.component.html',
  styleUrls: ['./support-chat-widget.component.css']
})
export class SupportChatWidgetComponent implements OnInit, OnDestroy {
  private documentMasterService = inject(DocumentMasterService);
  private consoleLogService = inject(ConsoleLogCaptureService);
  private sanitizer = inject(DomSanitizer);
  // Raw HttpClient instead of TicketService/SupportProjectMaster/
  // TicketAssigneeService/DiscussionService — see the SPD_BASE_URL comment
  // above for why this widget builds its own SPD request URLs rather than
  // going through those shared services.
  private http = inject(HttpClient);
  private ngZone = inject(NgZone);
  private User = inject(UserLoginService).User();

  @ViewChild('messagesEl') messagesEl?: ElementRef<HTMLDivElement>;

  isOpen = false;
  view: WidgetView = 'menu';

  // The tip on the home menu rotates on its own (pure CSS). This only picks
  // which of the tips it starts from, so a different one greets you each time
  // the menu is shown. Keep MENU_TIP_COUNT equal to the number of
  // .support-chat-tip-text entries in the template.
  menuTipStart = 0;
  private static readonly MENU_TIP_COUNT = 10;

  activeTicketTab: TicketTab = 'Active';
  ticketTabs: TicketTab[] = ['Active', 'InProgress', 'Closed'];

  // Real data (spd.SupportTickets_ReadByTicketOwner, same as the
  // /supportdesk/SupportTickets page). Fetched once per tab per time the
  // Tickets view is opened (cached while flipping between tabs you've
  // already visited, refetched fresh the next time you open the view).
  tickets: Record<TicketTab, SupportTicketsDTO[]> = { Active: [], InProgress: [], Closed: [] };
  ticketsLoading: Record<TicketTab, boolean> = { Active: false, InProgress: false, Closed: false };
  ticketsLoaded: Record<TicketTab, boolean> = { Active: false, InProgress: false, Closed: false };

  // --- Chat state ---
  discussionId: number | null = null;
  // Captured off the same DiscussionMaster row the chat join already
  // resolves (CompanyRelation.SalesPersonId, synced server-side) — reused
  // as Create Ticket's AssignedTo so a client's ticket lands with their
  // actual account manager instead of nobody.
  private mySalesPersonId: number | null = null;
  chatLoading = false;
  chatMessages: ChatMessage[] = [];
  hasMoreHistory = false;
  loadingOlderMessages = false;
  sendingMessage = false;
  private oldestLoadedSrNo: number | null = null;
  private hubConnection: signalR.HubConnection | null = null;
  private audioCtx: AudioContext | null = null;

  // --- SalesPerson (support) multi-thread state ---
  // WhatsApp-style: Chat opens on a list of assigned client companies
  // (discussionId === null means "showing the list"); tapping one opens its
  // thread full-screen, same as any single-thread client would see.
  // JoinAllMyDiscussionsAsSalesPerson already joins every thread's live-push
  // group in one call, so switching between them never needs to rejoin
  // anything server-side — only the locally-loaded thread changes.
  salesPersonThreads: DiscussionMasterForSalesPersonDTO[] = [];
  unreadThreadIds = new Set<number>();
  // Per-thread unread message count (unreadThreadIds is just "has any unread",
  // used for the FAB's conversation-count badge) - shown on each list row.
  unreadCounts = new Map<number, number>();
  threadSearchQuery = '';

  messageText = '';
  attachedScreenshotUrl: string | null = null;
  capturingScreenshot = false;

  attachedLogUrl: string | null = null;
  attachedLogName: string | null = null;
  capturingLogs = false;

  // This widget is mounted once in the always-present app shell
  // (Home/home.component.html), so this fires exactly once per session,
  // right after a successful login — not lazily when the Chat tab is first
  // opened. That way the socket (or its REST fallback) is already resolved
  // by the time the user opens Chat, instead of them watching a loading
  // spinner for the full connect/join/fallback sequence on every visit.
  ngOnInit(): void {
    this.visibility.refresh();
    this.visibilitySub.add(this.visibility.hidden$.subscribe(hidden => {
      this.launcherHidden = hidden;
      if (hidden) this.isOpen = false;
    }));
    // Profile menu > Help & Support: the launcher is already un-hidden by the
    // service by the time this fires, so just open the panel.
    this.visibilitySub.add(this.visibility.openRequest$.subscribe(() => {
      this.launcherEntering = true;
      this.isOpen = true;
      this.view = 'menu';
      this.pickMenuTip();
      setTimeout(() => this.launcherEntering = false, 600);
    }));
    this.connectAndJoinChat();
  }

  ngOnDestroy(): void {
    this.visibilitySub.unsubscribe();
    this.hubConnection?.stop();
  }

  // --- Dismissing the launcher (X on its top-left corner) ---
  private visibility = inject(SupportWidgetVisibilityService);
  private visibilitySub = new Subscription();
  @ViewChild('launcherEl') launcherEl?: ElementRef<HTMLDivElement>;
  launcherHidden = false;
  launcherEntering = false;
  private dismissingLauncher = false;

  // Flies the launcher up to the user's profile button (where "Help & Support"
  // now lives), pulses that button, then remembers the choice. The animation is
  // cosmetic: if the profile button can't be found or animations are
  // unavailable, it just hides.
  dismissLauncher(event: Event): void {
    event.stopPropagation();
    const el = this.launcherEl?.nativeElement;
    if (!el || this.dismissingLauncher) return;
    this.dismissingLauncher = true;

    // Web Animations callbacks aren't patched by zone.js, so without ngZone.run
    // the launcher wasn't removed until the next unrelated change detection,
    // leaving a ghost button over the profile avatar that swallowed its click.
    const finish = () => {
      this.ngZone.run(() => {
        this.dismissingLauncher = false;
        this.visibility.hide();
      });
    };
    el.style.pointerEvents = 'none';

    const target = Array.from(document.querySelectorAll<HTMLElement>('.cl-user-btn'))
      .find(t => t.getBoundingClientRect().width > 0);
    if (!target || typeof el.animate !== 'function'
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }

    const from = el.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
    const dy = (to.top + to.height / 2) - (from.top + from.height / 2);

    el.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0 },
        { transform: 'translate(0, -14px) scale(1.08)', opacity: 1, offset: 0.2 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.2)`, opacity: 0.15, offset: 1 }
      ],
      { duration: 750, easing: 'cubic-bezier(.5,0,.75,.4)', fill: 'forwards' }
    ).onfinish = () => {
      el.style.visibility = 'hidden';
      finish();
      target.animate(
        [
          { boxShadow: '0 0 0 0 rgba(66, 81, 200, 0.55)', transform: 'scale(1)' },
          { boxShadow: '0 0 0 12px rgba(66, 81, 200, 0)', transform: 'scale(1.06)' },
          { boxShadow: '0 0 0 0 rgba(66, 81, 200, 0)', transform: 'scale(1)' }
        ],
        { duration: 900, iterations: 1, easing: 'ease-out' }
      );
    };
  }

  toggleOpen(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.view = 'menu';
      this.pickMenuTip();
    }
  }

  private pickMenuTip(): void {
    this.menuTipStart = Math.floor(Math.random() * SupportChatWidgetComponent.MENU_TIP_COUNT);
  }

  dismiss(): void {
    this.isOpen = false;
  }

  openView(view: WidgetView): void {
    this.view = view;
    if (view === 'tickets') {
      this.ticketsLoaded = { Active: false, InProgress: false, Closed: false };
      this.loadTickets(this.activeTicketTab);
    } else if (view === 'chat') {
      // Guarded internally: a no-op once the socket is already up; otherwise
      // (re)tries the socket join, falling back to REST if that fails.
      this.connectAndJoinChat();
      if (this.discussionId !== null) this.clearUnread(this.discussionId);
      // History usually already loaded well before this view opened (see
      // ngOnInit) — loadMessageHistory()'s own scrollToBottom() ran before
      // the messages <div> even existed in the DOM, so it's a no-op then.
      // Scroll again now that the view (and #messagesEl) actually exists.
      this.scrollToBottom();
    }
  }

  get isSalesPerson(): boolean {
    return this.isProviderTenantUser();
  }

  activeThreadLabel(): string {
    return this.salesPersonThreads.find(t => t.discussionId === this.discussionId)?.clientCompanyName || '';
  }

  // True for the very first message and for the first message of any day
  // that differs from the one before it — drives the "Today"/"Yesterday"/
  // date pill shown above that message, same convention as WhatsApp/Slack.
  isNewDay(index: number): boolean {
    if (index === 0) return true;
    const prev = this.chatMessages[index - 1].createdOn;
    const curr = this.chatMessages[index].createdOn;
    if (!curr) return false;
    if (!prev) return true;
    return new Date(prev).toDateString() !== new Date(curr).toDateString();
  }

  dateSeparatorLabel(date?: Date): string {
    if (!date) return '';
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  }

  get filteredSalesPersonThreads(): DiscussionMasterForSalesPersonDTO[] {
    const q = this.threadSearchQuery.trim().toLowerCase();
    if (!q) return this.salesPersonThreads;
    return this.salesPersonThreads.filter(t => (t.clientCompanyName || '').toLowerCase().includes(q));
  }

  clientInitial(name: string | undefined | null): string {
    return (name || '?').trim().charAt(0).toUpperCase() || '?';
  }

  private bumpUnread(discussionId: number): void {
    this.unreadThreadIds.add(discussionId);
    this.unreadCounts.set(discussionId, (this.unreadCounts.get(discussionId) || 0) + 1);
  }

  private clearUnread(discussionId: number): void {
    this.unreadThreadIds.delete(discussionId);
    this.unreadCounts.delete(discussionId);
  }

  // A client with no thread yet (discussionId null) can never have an
  // unread count - kept as a method rather than inlining
  // unreadCounts.get(thread.discussionId) in the template because
  // Map<number,...>.get() doesn't accept the null discussionId can now be.
  unreadCountFor(thread: DiscussionMasterForSalesPersonDTO): number {
    return thread.discussionId === null ? 0 : (this.unreadCounts.get(thread.discussionId) || 0);
  }

  // Opens one client's conversation full-screen (discussionId !== null hides
  // the list — see the template). A no-op beyond clearing its unread state
  // if it's already the one loaded.
  openThread(thread: DiscussionMasterForSalesPersonDTO): void {
    if (thread.discussionId === null) {
      this.startThreadWithClient(thread);
      return;
    }

    this.clearUnread(thread.discussionId);
    if (this.discussionId === thread.discussionId) return;

    this.discussionId = thread.discussionId;
    this.chatMessages = [];
    this.hasMoreHistory = false;
    this.oldestLoadedSrNo = null;
    this.chatLoading = true;
    this.loadMessageHistory();
    this.markThreadRead();
  }

  // A client assigned to this SalesPerson who hasn't started a conversation
  // yet (DiscussionMaster_ReadAllForSalesPerson now lists every
  // CompanyRelation assignment, not just existing threads - see the
  // 2026-09-23 patch). Creates the thread through the same ReadOrCreate
  // endpoint a client's own first open would use, then re-enters
  // openThread() now that it has a real id. Only reachable from openThread()
  // itself, never called for a thread that already exists.
  private startThreadWithClient(thread: DiscussionMasterForSalesPersonDTO): void {
    this.chatLoading = true;
    const data: DiscussionMasterReadOrCreateRequestDTO = {
      tenantId: environment.supportProviderTenantId,
      tenantBranchId: environment.supportProviderTenantBranchId,
      fromTenantId: thread.fromTenantId,
      actionUser: this.User.userId.toString()
    };
    this.http.post<DiscussionMasterDTO>(DISCUSSION_MASTER_URL + '/ReadOrCreate', data, SKIP_REWRITE_OPTIONS).subscribe({
      next: (res) => {
        thread.discussionId = res.discussionId;
        // JoinAllMyDiscussionsAsSalesPerson ran before this thread existed,
        // so its live-push group was never joined - do that now, best
        // effort (REST-fallback sessions have no hubConnection at all, and
        // that's fine, MarkAsRead/ReadByDiscussionId/Create all still work
        // over plain REST regardless).
        this.hubConnection?.invoke('JoinDiscussionGroup', this.User.userId, res.tenantId, res.discussionId)
          .catch(err => console.warn('JoinDiscussionGroup after starting a new thread failed:', err));
        this.openThread(thread);
      },
      error: () => { this.chatLoading = false; }
    });
  }

  // Leaves the currently-open client conversation and returns to the list,
  // without disconnecting the socket or losing the loaded thread state (a
  // client sending a follow-up while a SalesPerson is back on the list still
  // updates that thread's list row live via the ReceiveMessage handler).
  backToClientList(): void {
    this.discussionId = null;
  }

  // The create-ticket view is only ever entered from Tickets, so "back"
  // from there returns to Tickets rather than all the way to the root menu.
  // A SalesPerson inside a client's thread backs out to the client list
  // first, same as the create-ticket case, rather than jumping straight to
  // the root menu.
  goBack(): void {
    if (this.view === 'chat' && this.isSalesPerson && this.discussionId !== null) {
      this.backToClientList();
      return;
    }
    this.view = this.view === 'create-ticket' ? 'tickets' : 'menu';
    if (this.view === 'menu') this.pickMenuTip();
  }

  setTicketTab(tab: TicketTab): void {
    this.activeTicketTab = tab;
    this.loadTickets(tab);
  }

  private loadTickets(tab: TicketTab): void {
    if (this.ticketsLoaded[tab] || this.ticketsLoading[tab]) return;
    this.ticketsLoading[tab] = true;

    // Company-wide (every user at the client company sees the same lists),
    // scoped to the Provider tenant that actually owns the SupportTickets
    // rows — not this.User's own tenant (see supportProviderTenantId).
    const data: SupportTicketsReadByFromCompanyIdRequestDTO = {
      fromCompanyId: this.User.companyId,
      tenantId: environment.supportProviderTenantId,
      tenantBranchId: environment.supportProviderTenantBranchId,
      ticketStatus: tab,
      pageSize: 10,
      pageNo: 1
    };

    this.http.post<SupportTicketsList>(SUPPORT_TICKET_URL + '/ReadByFromCompanyId', data, SKIP_REWRITE_OPTIONS).subscribe({
      next: (res) => {
        this.tickets[tab] = res.items || [];
        this.ticketsLoaded[tab] = true;
        this.ticketsLoading[tab] = false;
      },
      error: () => {
        this.ticketsLoading[tab] = false;
      }
    });
  }

  // Redirects to the external Advin support site for this ticket's discussion.
  // The token is handed over in the URL fragment, not a postMessage: that
  // depended on window.opener surviving across origins, which browser tracking
  // protections (Brave shields and similar) silently block. The existing bearer
  // token is reused instead of a new CreateTicketAccessLink minting endpoint.
  goToTicket(ticket: SupportTicketsDTO): void {
    if (!ticket.ticketGuid) return;
    const token = localStorage.getItem('access_token') || '';
    // tenantId/branchId aren't sent here: support.html hardcodes the
    // Provider tenant (1/1) itself, since that's what CompanyConfigMiddleware
    // needs to resolve the centralized SupportDesk DB, regardless of which
    // client's user is viewing the ticket.
    // guid goes in the query (the shareable part); the token goes in the
    // fragment so it never reaches the server or IIS logs. No userId: the page
    // resolves it from the token (UL/ValidateToken), so a redirected user is
    // signed in without a prompt.
    window.open(
      `${environment.ticketSupportSiteUrl}?guid=${encodeURIComponent(ticket.ticketGuid)}#token=${encodeURIComponent(token)}`,
      '_blank');
  }

  // --- Create Ticket (Title / Project / Description only — a deliberately
  // minimal quick-add form, not the full dynamic UIC form the internal
  // admin console's own "Create New Ticket" modal uses) ---

  createTicketForm = new FormGroup({
    title: new FormControl('', Validators.required),
    projectId: new FormControl<number | null>(null, Validators.required),
    description: new FormControl('')
  });

  projects: SupportProjectMasterDTO[] = [];
  projectsLoaded = false;
  creatingTicket = false;
  createTicketError = false;

  openCreateTicket(): void {
    this.view = 'create-ticket';
    this.createTicketForm.reset({ title: '', projectId: null, description: '' });
    this.createTicketError = false;

    if (this.projectsLoaded) return;
    // ReadAll had no tenant scoping at all - every tenant's projects that
    // has ever created a SupportProjectMaster row showed up here for every
    // client. ReadByTenantId, pinned to the Provider (1/1) like every other
    // SPD call this widget makes, scopes it to just Advin's own projects.
    const data: SupportProjectMasterReadByTenantIdRequestDTO = {
      tenantId: environment.supportProviderTenantId,
      tenantBranchId: environment.supportProviderTenantBranchId
    };
    this.http.post<SupportProjectMasterList>(SUPPORT_PROJECT_MASTER_URL + '/ReadByTenantId', data, SKIP_REWRITE_OPTIONS).subscribe({
      next: (res) => {
        this.projects = res.items || [];
        this.projectsLoaded = true;
      }
    });
  }

  submitCreateTicket(): void {
    this.createTicketForm.markAllAsTouched();
    if (this.createTicketForm.invalid || this.creatingTicket) return;

    const { title, description, projectId } = this.createTicketForm.value;
    this.creatingTicket = true;
    this.createTicketError = false;

    const now = new Date();
    const payload: SupportTicketsCreateRequestDTO = {
      // projectId was bound and selectable in the form but never actually
      // read here - every ticket got masterId:0/masterType:'' regardless of
      // what was picked. Same masterId/masterType: 'SupportProject'
      // convention the admin-side create-ticket form already uses.
      masterId: projectId || 0,
      masterType: projectId ? 'SupportProject' : '',
      ticketNo: '',
      tenantId: environment.supportProviderTenantId,
      tenantBranchId: environment.supportProviderTenantBranchId,
      fromCompanyId: this.User.companyId,
      toCompanyId: environment.supportProviderTenantId,
      title: title || '',
      tDesc: description || '',
      tType: '',
      category: '',
      tagList: '',
      ticketOwner: '',
      assignedTo: this.mySalesPersonId?.toString() || '',
      ticketStatus: 'Open',
      tPriority: '',
      affectsCustomer: '',
      appVersion: '',
      dueDate: now,
      estimatedDuration: '',
      actualDuration: '',
      targetDate: now,
      resolutionDate: now,
      department: '',
      raisedBy: this.User.userName || '',
      addField3: '',
      addField4: '',
      addField5: '',
      otherDetail1: '',
      otherDetail2: '',
      otherDetail3: '',
      otherDetail4: '',
      otherDetail5: '',
      otherDetail6: '',
      actionUser: this.User.userId.toString()
    };

    this.http.post<SupportTicketsDTO>(SUPPORT_TICKET_URL + '/Create', payload, SKIP_REWRITE_OPTIONS).subscribe({
      next: (res) => {
        this.creatingTicket = false;
        this.activeTicketTab = 'Active';
        this.ticketsLoaded.Active = false;
        this.openView('tickets');
        this.createTicketAssignee(res.ticketId);
      },
      error: () => {
        this.creatingTicket = false;
        this.createTicketError = true;
      }
    });
  }

  // Doesn't block the create-ticket flow or surface an error if this fails —
  // the ticket itself already exists and is visible via its flat AssignedTo
  // field either way; this only adds it to the proper multi-assignee system
  // the ticket-details page reads from. Skipped entirely if we never
  // resolved an assigned salesperson for this client.
  private createTicketAssignee(ticketId: number): void {
    if (!this.mySalesPersonId) return;

    const data: TicketAssigneeCreateRequestDTO = {
      ticketId,
      tenantId: environment.supportProviderTenantId,
      tenantBranchId: environment.supportProviderTenantBranchId,
      assignedTo: this.mySalesPersonId.toString(),
      workRatio: 100,
      assignDesc: '',
      aStatus: 'New',
      actionUser: this.User.userId.toString()
    };
    this.http.post<TicketAssigneeDTO>(TICKET_ASSIGNEE_URL + '/Create', data, SKIP_REWRITE_OPTIONS).subscribe({ error: () => {} });
  }

  // --- Chat ---

  // Socket-first per the backend's integration reference: connects the hub
  // and invokes JoinMyDiscussionAsClient, which resolves/creates the thread
  // AND joins its live-push group in one round trip. Guarded by
  // hubConnection so re-opening the Chat tab is a no-op once it's up; if it's
  // not up yet (first open, or a previous attempt failed), this retries it.
  private connectAndJoinChat(): void {
    if (this.hubConnection) return;
    if (this.discussionId === null) this.chatLoading = true;

    // Pinned to the Provider tenant (1/1), not this.User's own — same reason
    // as SKIP_REWRITE_OPTIONS/FORCE_PROVIDER_TENANT_HEADERS above:
    // DiscussionHub.ResolveConnectionString() resolves the DB connection
    // from these query params the same way CompanyConfigMiddleware resolves
    // it from HTTP headers, and SupportDesk's tables only exist in the
    // Provider's own database.
    const params = new URLSearchParams({
      companyId: String(environment.supportProviderTenantId),
      branchId: String(environment.supportProviderTenantBranchId),
      softwareId: String(environment.SoftwareId)
    });

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${DISCUSSION_DETAIL_URL}/discussionHub?${params.toString()}`, {
        accessTokenFactory: () => localStorage.getItem('access_token') || '',
        // Auth here is the Bearer token above, not cookies — and the SPD
        // gateway's CORS policy answers with a wildcard Access-Control-
        // Allow-Origin, which the browser refuses for a credentialed
        // (cookie-carrying) request. Turning credentials off avoids needing
        // a gateway-side CORS change for a connection that never used
        // cookies to begin with.
        withCredentials: false
      })
      .withAutomaticReconnect()
      .build();

    connection.on('ReceiveMessage', (msg: DiscussionDetailDTO) => {
      // @microsoft/signalr's WebSocket transport isn't reliably picked up by
      // zone.js's patches, so state changes made directly inside this
      // callback can update the component fine while never triggering a
      // re-render — the UI only catches up next time something ELSE (a
      // click, a timer) happens to run change detection. Wrapping in
      // ngZone.run() makes every push actually paint immediately.
      this.ngZone.run(() => {
        // Our own message already shows optimistically from sendMessage() —
        // this echo is only useful for what the OTHER party sends.
        if (msg.senderType === this.mySenderType() && msg.senderId === this.User.userId) return;

        // Only skip the unread flag if this exact thread is on screen right
        // now — panel open AND the Chat view AND this message's own thread.
        // Anything else (widget minimized, on Tickets/menu, or — for a
        // SalesPerson — a different client's tab) is an unseen message.
        const isVisibleNow = this.isOpen && this.view === 'chat' && this.discussionId === msg.discussionId;

        if (this.discussionId !== null && msg.discussionId === this.discussionId) {
          this.chatMessages = [...this.chatMessages, this.toChatMessage(msg)];
          this.scrollToBottom();
        }

        if (this.isSalesPerson) {
          // Bump this thread's list row — whether or not it's the one
          // currently open — and float it to the top, WhatsApp-style, so the
          // list is never stale by the time the SalesPerson backs out to it.
          const thread = this.salesPersonThreads.find(t => t.discussionId === msg.discussionId);
          if (thread) {
            thread.lastActivityOn = msg.createdOn;
            this.salesPersonThreads = [...this.salesPersonThreads].sort((a, b) =>
              new Date(b.lastActivityOn ?? 0).getTime() - new Date(a.lastActivityOn ?? 0).getTime()
            );
          } else {
            // No row exists for this thread at all — a brand-new client
            // conversation just got assigned to this SalesPerson mid-session
            // (after the initial JoinAllMyDiscussionsAsSalesPerson snapshot).
            // The message payload alone doesn't carry the client's company
            // name, so refresh the list from REST to actually get a row.
            this.refreshSalesPersonThreads();
          }
        }

        if (!isVisibleNow) {
          this.bumpUnread(msg.discussionId);
          this.playNotificationSound();
          // Support activity overrides a dismissed launcher: bring it back
          // (with its unread badge) so the reply isn't missed.
          if (this.launcherHidden) {
            this.launcherEntering = true;
            this.visibility.restore();
            setTimeout(() => this.launcherEntering = false, 600);
          }
        }
      });
    });

    // withAutomaticReconnect() opens a brand-new underlying connection after
    // any drop — it does NOT remember previous group membership. Without
    // this, live delivery silently stops after any network blip.
    connection.onreconnected(() => {
      this.joinHub(connection).catch(err => console.warn('Rejoin after reconnect failed:', err));
    });

    connection.start()
      .then(() => this.joinHub(connection))
      .then(() => {
        this.hubConnection = connection;
      })
      .catch(err => {
        console.warn('DiscussionHub live connection unavailable — falling back to REST-only chat:', err);
        connection.stop();
        this.ensureDiscussionReadyViaRest();
      });
  }

  // A logged-in user at the Provider tenant itself (Advin's own staff) is a
  // SalesPerson serving every client company's thread, not a Client of their
  // own tenant — joining as a Client here would just create a bogus
  // Provider-talking-to-itself thread. Every other tenant is a normal client.
  private isProviderTenantUser(): boolean {
    // UserLoginService.User() is a raw JSON.parse of a stored string — its
    // companyId/loggedInBranchId come back as runtime strings despite the
    // UserLoginResponseDTO type claiming number, so this must coerce before
    // comparing against the numeric environment config or it silently never
    // matches.
    return Number(this.User.companyId) === environment.supportProviderTenantId
      && Number(this.User.loggedInBranchId) === environment.supportProviderTenantBranchId;
  }

  private mySenderType(): DiscussionSenderType {
    return this.isProviderTenantUser() ? 'SalesPerson' : 'Client';
  }

  private joinHub(connection: signalR.HubConnection): Promise<void> {
    return this.isProviderTenantUser() ? this.joinAsSalesPerson(connection) : this.joinAsClient(connection);
  }

  private joinAsClient(connection: signalR.HubConnection): Promise<void> {
    return connection
      .invoke<DiscussionMasterDTO>(
        'JoinMyDiscussionAsClient',
        this.User.userId,
        environment.supportProviderTenantId,
        environment.supportProviderTenantBranchId
      )
      .then(thread => {
        const firstJoin = this.discussionId === null;
        this.discussionId = thread.discussionId;
        this.mySalesPersonId = thread.salesPersonId ?? null;
        if (firstJoin) {
          this.loadMessageHistory();
          this.markThreadRead();
        }
      });
  }

  // Joins every thread assigned to this SalesPerson in one call, so live
  // push works for all of them at once — openThread() then just swaps which
  // one's history is loaded, no rejoin needed when switching tabs.
  private joinAsSalesPerson(connection: signalR.HubConnection): Promise<void> {
    return connection
      .invoke<DiscussionMasterForSalesPersonDTO[]>('JoinAllMyDiscussionsAsSalesPerson', this.User.userId)
      .then(threads => this.selectMostRecentThread(threads));
  }

  // Populates the WhatsApp-style client list. Deliberately does NOT open any
  // thread - Chat lands on the list (discussionId stays null on a fresh
  // join), same as opening WhatsApp always shows the chat list rather than
  // resuming whichever conversation happened to be most recent.
  private selectMostRecentThread(threads: DiscussionMasterForSalesPersonDTO[]): void {
    this.salesPersonThreads = [...threads].sort((a, b) =>
      new Date(b.lastActivityOn ?? 0).getTime() - new Date(a.lastActivityOn ?? 0).getTime()
    );
    this.chatLoading = false;
  }

  // Re-fetches the assigned-thread list without touching whichever thread
  // is currently open (if any) - unlike selectMostRecentThread() on a fresh
  // join, this never changes discussionId, it just makes sure a row exists
  // for every thread the backend now has us assigned to (see the
  // ReceiveMessage handler above), and keeps the list's activity order fresh.
  private refreshSalesPersonThreads(): void {
    const data: DiscussionMasterReadAllForSalesPersonRequestDTO = {
      tenantId: environment.supportProviderTenantId,
      tenantBranchId: environment.supportProviderTenantBranchId,
      salesPersonId: this.User.userId,
      pageSize: 50,
      pageNo: 1
    };
    this.http.post<DiscussionMasterReadAllForSalesPersonResponseDTO>(DISCUSSION_MASTER_URL + '/ReadAllForSalesPerson', data, SKIP_REWRITE_OPTIONS).subscribe({
      next: (res) => {
        this.salesPersonThreads = [...(res.items || [])].sort((a, b) =>
          new Date(b.lastActivityOn ?? 0).getTime() - new Date(a.lastActivityOn ?? 0).getTime()
        );
      },
      error: () => {}
    });
  }

  // Fallback when the socket can't connect or the join call fails (e.g. the
  // WebSocket Protocol feature isn't enabled on this host yet) — same
  // lookup the hub call does, over plain REST, so history and sending still
  // work without live push.
  private ensureDiscussionReadyViaRest(): void {
    if (this.discussionId !== null) return;
    this.chatLoading = true;

    if (this.isProviderTenantUser()) {
      const salesPersonData: DiscussionMasterReadAllForSalesPersonRequestDTO = {
        tenantId: environment.supportProviderTenantId,
        tenantBranchId: environment.supportProviderTenantBranchId,
        salesPersonId: this.User.userId,
        pageSize: 50,
        pageNo: 1
      };
      this.http.post<DiscussionMasterReadAllForSalesPersonResponseDTO>(DISCUSSION_MASTER_URL + '/ReadAllForSalesPerson', salesPersonData, SKIP_REWRITE_OPTIONS).subscribe({
        next: (res) => this.selectMostRecentThread(res.items || []),
        error: () => { this.chatLoading = false; }
      });
      return;
    }

    const data: DiscussionMasterReadOrCreateRequestDTO = {
      tenantId: environment.supportProviderTenantId,
      tenantBranchId: environment.supportProviderTenantBranchId,
      fromTenantId: this.User.companyId,
      fromTenantBranchId: this.User.loggedInBranchId,
      actionUser: this.User.userId.toString()
    };

    this.http.post<DiscussionMasterDTO>(DISCUSSION_MASTER_URL + '/ReadOrCreate', data, SKIP_REWRITE_OPTIONS).subscribe({
      next: (res) => {
        this.discussionId = res.discussionId;
        this.mySalesPersonId = res.salesPersonId ?? null;
        this.loadMessageHistory();
        this.markThreadRead();
      },
      error: () => {
        this.chatLoading = false;
      }
    });
  }

  private markThreadRead(): void {
    // 0 is a valid discussionId (TenantMaxIdCounter_GetMaxId's very first
    // reservation for a brand-new tenant/table pair is 0, not 1) - a truthy
    // check here silently no-ops forever on that thread instead of erroring,
    // which is exactly what left new tenants' first chat stuck on "Loading…".
    if (this.discussionId === null) return;
    const data: DiscussionDetailMarkAsReadRequestDTO = {
      tenantId: environment.supportProviderTenantId,
      tenantBranchId: environment.supportProviderTenantBranchId,
      discussionId: this.discussionId,
      readerSenderType: this.mySenderType(),
      actionUser: this.User.userId.toString()
    };
    this.http.post<void>(DISCUSSION_DETAIL_URL + '/MarkAsRead', data, SKIP_REWRITE_OPTIONS).subscribe({ error: () => {} });
  }

  private loadMessageHistory(beforeSrNo?: number): void {
    if (this.discussionId === null) return;
    const loadingOlder = beforeSrNo != null;
    if (loadingOlder) this.loadingOlderMessages = true;

    const data: DiscussionDetailReadByDiscussionIdRequestDTO = {
      tenantId: environment.supportProviderTenantId,
      tenantBranchId: environment.supportProviderTenantBranchId,
      discussionId: this.discussionId,
      pageSize: 20,
      ...(loadingOlder ? { beforeSrNo } : {})
    };

    // Sorted ascending regardless of the API's own order — display always
    // wants oldest-at-top, and this way it's correct either way.
    this.http.post<DiscussionDetailReadByDiscussionIdResponseDTO>(DISCUSSION_DETAIL_URL + '/ReadByDiscussionId', data, SKIP_REWRITE_OPTIONS).subscribe({
      next: (res) => {
        const items = (res.items || [])
          .map(m => this.toChatMessage(m))
          .sort((a, b) => a.srNo - b.srNo);

        if (loadingOlder) {
          this.prependOlderMessages(items);
          this.loadingOlderMessages = false;
        } else {
          this.chatMessages = items;
          this.chatLoading = false;
          this.scrollToBottom();
        }

        // hasMore rides on every row of the page (same value repeated), not
        // on the response envelope — confirmed against the live API.
        this.hasMoreHistory = !!res.items?.[0]?.hasMore;
        if (items.length) {
          this.oldestLoadedSrNo = items[0].srNo;
        }
      },
      error: () => {
        this.chatLoading = false;
        this.loadingOlderMessages = false;
      }
    });
  }

  // Keeps the scroll position stable when older messages are prepended above
  // what's currently in view — otherwise the whole list jumps as its height
  // grows above the reader.
  private prependOlderMessages(items: ChatMessage[]): void {
    const el = this.messagesEl?.nativeElement;
    const heightBefore = el?.scrollHeight ?? 0;

    this.chatMessages = [...items, ...this.chatMessages];

    if (!el) return;
    setTimeout(() => {
      el.scrollTop = el.scrollHeight - heightBefore;
    });
  }

  // A short, soft two-note ping (880Hz -> 1320Hz, under 200ms) for an
  // unseen incoming message — synthesized via Web Audio instead of an audio
  // asset, so there's nothing to source/license/host. Never blocks chat if
  // audio is unavailable or blocked (e.g. no user gesture on the page yet).
  private playNotificationSound(): void {
    try {
      this.audioCtx ??= new AudioContext();
      const ctx = this.audioCtx;
      if (ctx.state === 'suspended') ctx.resume();

      const now = ctx.currentTime;
      [[880, now], [1320, now + 0.09]].forEach(([freq, start]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.15, start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.12);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.13);
      });
    } catch {
      // Sound is a nice-to-have — never surface this as a chat error.
    }
  }

  private scrollToBottom(): void {
    // Reading messagesEl must happen INSIDE the timeout, not before it —
    // called right after `view` flips to 'chat', the *ngIf for the messages
    // <div> hasn't rendered yet, so reading the ViewChild synchronously here
    // would always see the stale (undefined) reference from before Angular
    // updates the DOM.
    setTimeout(() => {
      const el = this.messagesEl?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  onMessagesScroll(event: Event): void {
    const el = event.target as HTMLDivElement;
    if (el.scrollTop < 40 && this.hasMoreHistory && !this.loadingOlderMessages && this.oldestLoadedSrNo != null) {
      this.loadMessageHistory(this.oldestLoadedSrNo);
    }
  }

  private toChatMessage(m: DiscussionDetailDTO): ChatMessage {
    return {
      discussionDetailId: m.discussionDetailId,
      srNo: m.srNo,
      senderType: m.senderType,
      senderId: m.senderId,
      senderName: m.senderName,
      createdOn: m.createdOn,
      fromMe: m.senderType === this.mySenderType() && m.senderId === this.User.userId,
      isRead: !!m.isRead,
      html: m.discussionText
    };
  }

  // Hides the panel (so it isn't captured), snapshots the page with
  // html2canvas, and uploads it the same way uploadScreenshotFile() does.
  // This is a full auto-capture of the current page — distinct from
  // onPaste() below, which attaches whatever image the user already copied
  // (e.g. a Win+Shift+S snip), not the whole page.
  captureScreenshot(): void {
    if (this.capturingScreenshot) return;
    this.capturingScreenshot = true;
    const wasOpen = this.isOpen;
    this.isOpen = false;

    setTimeout(() => {
      html2canvas(document.body, { useCORS: true, scale: 1 })
        .then(canvas => new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png')))
        .then(blob => {
          this.isOpen = wasOpen;
          if (!blob) {
            this.capturingScreenshot = false;
            return;
          }
          this.uploadScreenshotFile(new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' }));
        })
        .catch(() => {
          this.isOpen = wasOpen;
          this.capturingScreenshot = false;
        });
    }, 50);
  }

  // Lets a Windows Snipping Tool capture (Win+Shift+S copies the snip to the
  // clipboard as an image) — or any other image on the clipboard — be
  // pasted straight into the message box instead of needing the camera
  // button. Bound to (paste) on the message <input>.
  onPaste(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          this.capturingScreenshot = true;
          this.uploadScreenshotFile(file);
        }
        break;
      }
    }
  }

  private uploadScreenshotFile(file: File): void {
    // A pasted clipboard image often has no real filename.
    const fileName = file.name || `pasted-image-${Date.now()}.png`;
    const fd = new FormData();
    fd.append('file', file, fileName);

    this.documentMasterService.DocumentMaster_Upload(fd).subscribe({
      next: (resp: any) => {
        this.attachedScreenshotUrl = resp?.url ?? resp?.path ?? resp?.file ?? '';
        this.capturingScreenshot = false;
      },
      error: () => {
        this.capturingScreenshot = false;
      }
    });
  }

  removeAttachedScreenshot(): void {
    this.attachedScreenshotUrl = null;
  }

  // Same upload path as the screenshot, just with a text file built from
  // ConsoleLogCaptureService's buffer instead of a canvas capture.
  attachConsoleLogs(): void {
    if (this.capturingLogs) return;
    this.capturingLogs = true;

    const fileName = `console-log-${Date.now()}.txt`;
    const file = new File([this.consoleLogService.getLogText()], fileName, { type: 'text/plain' });
    const fd = new FormData();
    fd.append('file', file, file.name);

    this.documentMasterService.DocumentMaster_Upload(fd).subscribe({
      next: (resp: any) => {
        this.attachedLogUrl = resp?.url ?? resp?.path ?? resp?.file ?? '';
        this.attachedLogName = fileName;
        this.capturingLogs = false;
      },
      error: () => {
        this.capturingLogs = false;
      }
    });
  }

  removeAttachedLog(): void {
    this.attachedLogUrl = null;
    this.attachedLogName = null;
  }

  sendMessage(): void {
    const text = this.messageText.trim();
    if ((!text && !this.attachedScreenshotUrl && !this.attachedLogUrl) || this.discussionId === null || this.sendingMessage) return;

    // Styled with inline style="" attributes, not CSS classes: this HTML is
    // injected via [innerHTML], so it never gets Angular's emulated view-
    // encapsulation attribute and a scoped class rule would never match it.
    let html = '';
    if (text) html += `<p>${this.escapeHtml(text)}</p>`;
    if (this.attachedScreenshotUrl) {
      html += `<img src="${this.escapeAttr(this.attachedScreenshotUrl)}" alt="Screenshot" `
        + `style="max-width:100%;border-radius:8px;margin-top:4px;display:block;" />`;
    }
    if (this.attachedLogUrl) {
      html += `<p><a href="${this.escapeAttr(this.attachedLogUrl)}" target="_blank" rel="noopener" `
        + `style="color:inherit;text-decoration:underline;">`
        + `📎 ${this.escapeHtml(this.attachedLogName || 'console-log.txt')}</a></p>`;
    }

    const savedText = this.messageText;
    const savedScreenshot = this.attachedScreenshotUrl;
    const savedLog = this.attachedLogUrl;
    const savedLogName = this.attachedLogName;

    this.messageText = '';
    this.attachedScreenshotUrl = null;
    this.removeAttachedLog();
    this.sendingMessage = true;

    // Shown immediately (bypassing sanitization since we composed this
    // markup ourselves, see escapeHtml() below) rather than waiting for the
    // REST round-trip or the hub echo — sendMessage()'s own client filters
    // that echo back out (see connectHub()) specifically to avoid a
    // duplicate once this optimistic entry is already on screen.
    const senderType = this.mySenderType();
    const optimistic: ChatMessage = {
      discussionDetailId: 0,
      srNo: (this.chatMessages[this.chatMessages.length - 1]?.srNo ?? 0) + 1,
      senderType,
      senderId: this.User.userId,
      senderName: this.User.userName || 'You',
      createdOn: new Date(),
      fromMe: true,
      isRead: false,
      html: this.sanitizer.bypassSecurityTrustHtml(html)
    };
    this.chatMessages = [...this.chatMessages, optimistic];
    this.scrollToBottom();

    const payload: DiscussionDetailCreateRequestDTO = {
      tenantId: environment.supportProviderTenantId,
      tenantBranchId: environment.supportProviderTenantBranchId,
      discussionId: this.discussionId,
      senderType,
      senderId: this.User.userId,
      discussionText: html,
      actionUser: this.User.userId.toString()
    };

    this.http.post<DiscussionDetailDTO>(DISCUSSION_DETAIL_URL + '/Create', payload, SKIP_REWRITE_OPTIONS).subscribe({
      next: (res) => {
        this.sendingMessage = false;
        optimistic.discussionDetailId = res.discussionDetailId;
        optimistic.srNo = res.srNo;
      },
      error: () => {
        this.sendingMessage = false;
        this.chatMessages = this.chatMessages.filter(m => m !== optimistic);
        this.messageText = savedText;
        this.attachedScreenshotUrl = savedScreenshot;
        this.attachedLogUrl = savedLog;
        this.attachedLogName = savedLogName;
      }
    });
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Same as escapeHtml but for values going inside a "..." attribute (a URL
  // here) rather than between tags — only the character that could break
  // out of the quotes actually matters.
  private escapeAttr(value: string): string {
    return value.replace(/"/g, '%22');
  }
}
